import { useQuery } from '@apollo/client'
import { UserProvider } from '@faceup/member'
import { Chat, ChatInput, useUnreadFollowUpMessages } from '@faceup/report'
import { Card } from '@faceup/ui'
import { Alert, Input, Typography } from '@faceup/ui-base'
import { ChannelType, compact, NUMBER_FOR_NO_LIMIT } from '@faceup/utils'
import type { ResultOf } from '@graphql-typed-document-node/core'
import { type ComponentProps, useMemo, useState } from 'react'
import { graphql } from '../__generated__'
import { defineMessages, FormattedMessage, useIntl } from '../TypedIntl'
import useAnalytics from '../utils/analytics'

const ReportChatQuery = graphql(`
  query ReportChatQuery($followUpFirst: Int!) {
    companyVictimViewer {
      id
      case {
        id
        status {
          id
          groupName
        }
        channel {
          id
          sourceType
          defaultLanguage
        }
        followUpActivities(first: $followUpFirst) {
          __typename
          items {
            id
            ...useUnreadFollowUpMessages_followupComment
          }
        }
      }
    }
  }
`)

const messages = defineMessages({
  title: 'Shared.chat.title',
  messagingInfo: 'FollowUp.ReportSent.chat.messagingInfo',
  writeNote: 'FollowUp.chat.write',
  empty: 'FollowUp.chat.empty',
  noteLabel: 'Shared.chat.label',
  administrator: 'FollowUp.chat.administrator',
  leaveQuestion: 'FollowUp.chat.leaveQuestion',
  reopen: 'FollowUp.ReportStatusAlert.reopen',
  addAttachments: 'Shared.report.attachments.add',
  attachmentsTitle: 'Shared.report.attachments',
  detectLanguage: 'Shared.report.translate.detectLanguage',
  translate: 'Shared.report.translate.translate',
  showOriginal: 'Shared.report.translate.showOriginal',
  to: 'Shared.report.translate.to',
  sameLanguageError: 'Shared.report.translate.error.sameLanguages',
  disabledReasonE2EE: 'Shared.report.translate.error.disabledReasonE2EE',
  onUseWarningTitle: 'Shared.report.translate.onUseWarning.title',
  onUseWarningContent: 'Shared.report.translate.onUseWarning.content',
  onUseWarningDoNotShowAgain: 'Shared.report.translate.onUseWarning.doNotShowAgain',
})

const closedMessagesPerChannel = defineMessages<ChannelType>({
  [ChannelType.Survey]: 'FollowUp.ReportStatusAlert.submissionWasClosed',
  [ChannelType.AutomatedHotline]: 'FollowUp.ReportStatusAlert.reportWasClosed',
  [ChannelType.LiveHotline]: 'FollowUp.ReportStatusAlert.reportWasClosed',
  [ChannelType.AiHotline]: 'FollowUp.ReportStatusAlert.reportWasClosed',
  [ChannelType.ReportingSystem]: 'FollowUp.ReportStatusAlert.reportWasClosed',
})

const archivedMessagesPerChannel = defineMessages<ChannelType>({
  [ChannelType.Survey]: 'FollowUp.ReportStatusAlert.submissionWasArchived',
  [ChannelType.AutomatedHotline]: 'FollowUp.ReportStatusAlert.reportWasArchived',
  [ChannelType.LiveHotline]: 'FollowUp.ReportStatusAlert.reportWasArchived',
  [ChannelType.AiHotline]: 'FollowUp.ReportStatusAlert.reportWasArchived',
  [ChannelType.ReportingSystem]: 'FollowUp.ReportStatusAlert.reportWasArchived',
})

type ReportChatQuery = ResultOf<typeof ReportChatQuery>

export const ReportChatPreview = () => {
  const { formatMessage } = useIntl()
  return (
    <Card className='p-8 w-full flex flex-col gap-4'>
      <Typography.Title level={4} className='m-0!'>
        <FormattedMessage {...messages.title} />
      </Typography.Title>
      <Alert type='warning' message={<FormattedMessage {...messages.messagingInfo} />} />
      <Input.TextArea rows={3} placeholder={formatMessage(messages.writeNote)} disabled />
    </Card>
  )
}

type ReportChatProps = {
  isLoggedIn: boolean
}

export const ReportChat = ({ isLoggedIn }: ReportChatProps) => {
  const { formatMessage } = useIntl()
  const { track } = useAnalytics()
  const [alertDismissed, setAlertDismissed] = useState(false)

  const { data, loading } = useQuery(ReportChatQuery, {
    fetchPolicy: 'network-only',
    skip: !isLoggedIn,
    variables: { followUpFirst: NUMBER_FOR_NO_LIMIT },
  })

  if (!isLoggedIn || loading) {
    return <Card loading className='w-full p-8' />
  }

  const viewer = data?.companyVictimViewer
  const aCase = viewer?.case

  if (!viewer || !aCase) {
    return <Card loading className='w-full p-8' />
  }

  const hasMessages = (aCase.followUpActivities?.items?.length ?? 0) > 0

  const showAlert = !hasMessages && !alertDismissed

  return (
    <UserProvider
      member={null}
      application='follow-up'
      userId={viewer.id}
      onChangeLanguage={() => null}
      onLogout={() => null}
    >
      <Card className='p-8 w-full flex flex-col gap-4' data-test='report-chat'>
        <Typography.Title level={4} className='m-0!'>
          <FormattedMessage {...messages.title} />
        </Typography.Title>
        {showAlert && (
          <Alert
            type='warning'
            closable
            onClose={() => setAlertDismissed(true)}
            message={<FormattedMessage {...messages.messagingInfo} />}
          />
        )}
        <ChatInner
          viewer={viewer}
          onMessageSend={() => {
            track('follow_up_message_sent')
            setAlertDismissed(true)
          }}
          caseId={aCase.id}
          userId={viewer.id}
          relativeMessages={{
            type: 'Victim',
            ...messages,
            closedMessagesPerChannel,
            archivedMessagesPerChannel,
          }}
          input={props => (
            <ChatInput
              {...props}
              inputRef={null}
              mentions={null}
              placeholder={formatMessage(messages.writeNote)}
            />
          )}
        />
      </Card>
    </UserProvider>
  )
}

type ChatInnerProps = {
  viewer: NonNullable<ReportChatQuery['companyVictimViewer']>
} & Omit<ComponentProps<typeof Chat>, 'firstUnreadMessageId'>

const ChatInner = ({ viewer, ...props }: ChatInnerProps) => {
  const comments = useMemo(() => compact(viewer.case?.followUpActivities?.items ?? []), [viewer])
  const { firstUnreadMessageId } = useUnreadFollowUpMessages(comments)

  return <Chat {...props} firstUnreadMessageId={firstUnreadMessageId ?? ''} />
}
